import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import {
  ComponentProps,
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useState,
} from 'react';
import { FormattedMessage as T, useIntl } from 'react-intl';
import { cn } from '../../lib/classNames';
import { Cloud$SuggestedAddress } from '../../types/cloudApi';
import { Cloud$MainHardwareType } from '../../types/cloudEnums';
import { Otovo$Locale } from '../../types/otovoweb';
import { useAddressValidation } from '../InterestRegistration/AddressSearch/AddressValidation/useAddressValidation';
import { useAddressSuggestions } from '../InterestRegistration/AddressSearch/useAddressSuggestions';
import Spinner from '../LoadingScreens/Spinner';
import { ErrorMessage, Field, Label } from './FormElements';
import m from './messages';

/**
 * AddressField - A form-friendly address search component
 *
 * Built on HeadlessUI and uses the same address suggestion/validation APIs as AddressSearchBar,
 * but designed to work within forms without handling redirects or interest creation.
 *
 * @example
 * import { AddressField } from '@otovo/shared/components/Form/AddressField';
 *
 * const MyForm = () => {
 *   const addressRef = useRef<AddressFieldImperativeActions>(null);
 *
 *   const handleSubmit = async (e) => {
 *     e.preventDefault();
 *     const addressId = await addressRef.current?.validateAddress();
 *     if (!addressId) {
 *       return; // Validation failed, error will be shown
 *     }
 *     // Submit form with addressId
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <AddressField
 *         ref={addressRef}
 *         locale="nb"
 *         mainHardwareType="pv_system"
 *       />
 *       <button type="submit">Submit</button>
 *     </form>
 *   );
 * };
 */

export type AddressFieldImperativeActions = {
  validateAddress: () => Promise<string | null>;
  setIsRequired: () => void;
};

type Props = {
  locale: Otovo$Locale;
  mainHardwareType: Cloud$MainHardwareType;
  required?: boolean;
  label?: string;
} & Omit<ComponentProps<'input'>, 'type' | 'value' | 'onChange'>;

const AddressField = forwardRef<AddressFieldImperativeActions, Props>(
  (
    {
      label,
      locale,
      mainHardwareType,
      required = true,
      className,
      disabled,
      placeholder,
    },
    ref,
  ) => {
    const intl = useIntl();

    const {
      address,
      setAddress,
      suggestions,
      suggestError,
      isLoadingSuggestions,
    } = useAddressSuggestions(locale, mainHardwareType);

    const [errorMessage, setErrorMessage] = useState<string>('');
    const { state: validationState, actions: validationActions } =
      useAddressValidation();
    const id = useId();

    const validateAddress = async (): Promise<string | null> => {
      if (validationState.status === 'pending') {
        // Already validating
        return null;
      }

      if (!address.text) {
        const message = intl.formatMessage(m.addressRequired);
        setErrorMessage(message);
        return null;
      }

      if (!address.id && suggestions && suggestions.length === 0) {
        const message = intl.formatMessage(m.addressRequired);
        setErrorMessage(message);
        return null;
      }

      try {
        const validAddress = await validationActions.validate(
          address,
          suggestions,
          'server',
        );
        if (validAddress) {
          setErrorMessage('');
          setAddress(validAddress);
          return validAddress.id;
        }
        return null;
      } catch (error) {
        return null;
      }
    };

    useImperativeHandle(ref, () => ({
      validateAddress,
      setIsRequired: () => {
        if (!address.text || !address.id) {
          setErrorMessage(intl.formatMessage(m.addressRequired));
        }
      },
    }));

    const handleAddressChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const newText = event.target.value;
      setAddress({ text: newText, id: '' });
      setErrorMessage('');
      validationActions.resetError();
    };

    const handleSelectAddress = (
      selectedAddress: Cloud$SuggestedAddress | null,
    ) => {
      if (selectedAddress) {
        setAddress(selectedAddress);
        setErrorMessage('');
        validationActions.resetError();
      }
    };

    // Sync validation state errors with local error state
    useEffect(() => {
      if (validationState.errorMessage) {
        setErrorMessage(validationState.errorMessage);
      }
    }, [validationState.errorMessage]);

    const isValidating = validationState.status === 'pending';
    const isLoading = isLoadingSuggestions || isValidating;
    const displayError =
      errorMessage || validationState.errorMessage || suggestError;

    return (
      <Field className={className}>
        <Label className="block w-full text-left">
          {label || <T {...m.addressLabel} />}
        </Label>
        <Combobox value={address} onChange={handleSelectAddress}>
          {({ open }) => (
            <div className="relative">
              <ComboboxInput
                disabled={disabled}
                id={id}
                required={required}
                className={cn(
                  'mt-2 mb-1 w-full rounded-sm border-0 pr-10 ring-1 transition-all duration-200 hover:ring-2 focus:ring-2 focus:outline-none',
                  'ring-grey-10 hover:ring-grey-10 focus:ring-digital',
                  displayError && 'ring-red hover:ring-red focus:ring-red',
                  'data-disabled:bg-grey-5',
                )}
                placeholder={
                  placeholder || intl.formatMessage(m.addressPlaceholder)
                }
                displayValue={(selectedAddress) =>
                  (selectedAddress as unknown as Cloud$SuggestedAddress)
                    ?.text || ''
                }
                onChange={handleAddressChange}
                onBlur={validateAddress}
              />

              {isLoading && (
                <div className="absolute top-1/2 right-3 -translate-y-1/2">
                  <Spinner size="w-4 h-4" color="text-grey-30" />
                </div>
              )}

              {suggestions && suggestions.length > 0 && open && (
                <ComboboxOptions
                  static
                  className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none"
                >
                  {suggestions.map((suggestion, index) => (
                    <ComboboxOption
                      key={suggestion.id || index}
                      value={suggestion}
                      className={({ focus }) =>
                        cn(
                          'relative cursor-default px-3 py-2 select-none',
                          focus ? 'bg-digital text-white' : 'text-gray-900',
                        )
                      }
                    >
                      <span className="block truncate">{suggestion.text}</span>
                    </ComboboxOption>
                  ))}
                </ComboboxOptions>
              )}

              {isLoading && (
                <div aria-live="polite" className="sr-only">
                  {isLoadingSuggestions
                    ? intl.formatMessage(m.loadingSuggestions)
                    : intl.formatMessage(m.validatingAddress)}
                </div>
              )}
            </div>
          )}
        </Combobox>

        {displayError && <ErrorMessage>{displayError}</ErrorMessage>}

        {/* Hidden input to store the address ID for form submission */}
        <input type="hidden" name="address_id" value={address.id || ''} />
      </Field>
    );
  },
);

AddressField.displayName = 'AddressField';

export default AddressField;
